import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap'
import DashboardLayout from '../../../layouts/Dashboard'

import styles from './styles.module.css'
import { useEffect, useState } from 'react'
import { Circle } from 'phosphor-react'
import { useLoading } from '../../../hooks/LoadingContext'
import api from '../../../services/api'
import { toast } from 'react-toastify'
import {
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryZoomContainer,
} from 'victory'
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subtractDays,
} from '../../../utils/date'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'

const today = new Date()
const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)

const periodOptions = [
  { value: 'today', label: 'Hoje', startDate: startOfDay(today), endDate: endOfDay(today) },
  {
    value: '7-days',
    label: 'Últimos 7 dias',
    startDate: startOfDay(subtractDays(today, 6)),
    endDate: endOfDay(today),
  },
  {
    value: '28-days',
    label: 'Últimos 28 dias',
    startDate: startOfDay(subtractDays(today, 27)),
    endDate: endOfDay(today),
  },
  {
    value: '90-days',
    label: 'Últimos 90 dias',
    startDate: startOfDay(subtractDays(today, 89)),
    endDate: endOfDay(today),
  },
  {
    value: 'this-week',
    label: 'Esta semana',
    startDate: startOfWeek(today),
    endDate: endOfWeek(today),
  },
  {
    value: 'this-month',
    label: 'Este mês',
    startDate: startOfMonth(today),
    endDate: endOfMonth(today),
  },
  {
    value: 'this-year',
    label: 'Este ano',
    startDate: startOfYear(today),
    endDate: endOfYear(today),
  },
  {
    value: 'last-week',
    label: 'Semana passada',
    startDate: startOfWeek(subtractDays(today, 7)),
    endDate: endOfWeek(subtractDays(today, 7)),
  },
  {
    value: 'last-month',
    label: 'Mês passado',
    startDate: startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1)),
    endDate: endOfMonth(new Date(today.getFullYear(), today.getMonth() - 1)),
  },
  { value: 'custom', label: 'Personalizado' },
]

const EntregasEstatisticas = () => {
  const [stats, setStats] = useState<any>(null)
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [selectedPeriod, setSelectedPeriod] = useState<any>(periodOptions[0])
  const [selectedDriverId, setSelectedDriverId] = useState<any>(null)
  const [selectedVehicleId, setSelectedVehicleId] = useState<any>(null)
  const [deliveredData, setDeliveredData] = useState<any[]>([])
  const [canceledData, setCanceledData] = useState<any[]>([])
  const [withProblemsData, setWithProblemsData] = useState<any[]>([])
  const [activeCharts, setActiveCharts] = useState<number[]>([1, 1, 1])

  const [zoomDomain, setZoomDomain] = useState<any>({ x: [0, 1] })
  const [chartTicks, setChartTicks] = useState<any>({
    x: [
      yesterday.toLocaleString('pt-BR', { day: 'numeric', month: '2-digit', timeZone: 'UTC' }),
      today.toLocaleString('pt-BR', { day: 'numeric', month: '2-digit', timeZone: 'UTC' }),
      tomorrow.toLocaleString('pt-BR', { day: 'numeric', month: '2-digit', timeZone: 'UTC' }),
    ],
    y: [0, 1, 2, 3, 4, 5, 6, 7],
  })

  const [drivers, setDrivers] = useState<any[]>([])
  const [vehicles, setVehicles] = useState<any[]>([])

  const { isLoading, setIsLoading } = useLoading()

  useEffect(() => {
    async function loadDrivers() {
      try {
        setIsLoading(true)
        const { data } = await api.get('/company/drivers')
        setDrivers(data.drivers)
      } catch (error) {
        toast.error('Erro ao carregar motoristas')
        console.log(error)
      }
    }

    async function loadVehicles() {
      try {
        setIsLoading(true)
        const { data } = await api.get('/company/vehicles')
        setVehicles(data)
      } catch (error) {
        toast.error('Erro ao carregar veículos')
        console.log(error)
      }
    }

    loadDrivers()
    loadVehicles()
  }, [setIsLoading])

  const handleFilter = async () => {
    if (!selectedPeriod) {
      toast.error('Selecione um período')
      return
    }

    try {
      setIsLoading(true)

      const period = selectedPeriod.value === 'custom' ? { startDate, endDate } : selectedPeriod
      const { startDate: periodStartDate, endDate: periodEndDate } = period

      if (!periodStartDate || !periodEndDate) {
        toast.error(
          'Você precisa informar a data de início e de término desejadas para o intervalo.',
        )
        return
      }

      if (periodStartDate > periodEndDate) {
        toast.error('A data de início deve ser anterior à data de término.')
        return
      }

      const params: Record<string, string> = {}
      params.start_period = periodStartDate.toISOString()
      params.end_period = periodEndDate.toISOString()
      if (selectedDriverId) params.driver_id = String(selectedDriverId)
      if (selectedVehicleId) params.vehicle_id = String(selectedVehicleId)

      const { data } = await api.get('/company/requests/stats', { params })
      setStats(data)
    } catch (error) {
      toast.error('Erro ao carregar estatísticas')
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    handleFilter()
    // eslint-disable-next-line
  }, [])

  const handleChartDataChange = () => {
    if (!stats) return

    let ticks = []

    let data = Object.values(
      stats?.delivered.reduce(
        (acc: Record<string, { x: string; y: number; iso: string }>, delivery: any) => {
          const isoDate = new Date(delivery.date).toISOString().split('T')[0] // YYYY-MM-DD para ordenação
          const formattedDate = new Date(delivery.date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            timeZone: 'UTC',
          })

          if (!acc[isoDate]) {
            acc[isoDate] = { x: formattedDate, y: 0, iso: isoDate }
          }
          acc[isoDate].y += 1

          return acc
        },
        {},
      ),
    ) as { x: string; y: number; iso: string }[]

    let sortedData = data.sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime())

    if (activeCharts[0]) ticks.push(...sortedData)

    setDeliveredData(sortedData)

    data = Object.values(
      stats?.canceled.reduce(
        (acc: Record<string, { x: string; y: number; iso: string }>, delivery: any) => {
          const isoDate = new Date(delivery.date).toISOString().split('T')[0]
          const formattedDate = new Date(delivery.date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            timeZone: 'UTC',
          })

          if (!acc[isoDate]) {
            acc[isoDate] = { x: formattedDate, y: 0, iso: isoDate }
          }
          acc[isoDate].y += 1

          return acc
        },
        {},
      ),
    ) as { x: string; y: number; iso: string }[]

    sortedData = data.sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime())

    if (activeCharts[1]) ticks.push(...sortedData)

    setCanceledData(sortedData)

    data = Object.values(
      stats?.with_problems.reduce(
        (acc: Record<string, { x: string; y: number; iso: string }>, delivery: any) => {
          const isoDate = new Date(delivery.date).toISOString().split('T')[0]
          const formattedDate = new Date(delivery.date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            timeZone: 'UTC',
          })

          if (!acc[isoDate]) {
            acc[isoDate] = { x: formattedDate, y: 0, iso: isoDate }
          }
          acc[isoDate].y += 1

          return acc
        },
        {},
      ),
    ) as { x: string; y: number; iso: string }[]

    sortedData = data.sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime())

    if (activeCharts[2]) ticks.push(...sortedData)

    setWithProblemsData(sortedData)

    const chartTicks = {
      x: Array.from(new Set(ticks.map((tick) => tick.x))),
      y: Array.from(new Set(ticks.map((tick) => tick.y))).sort((a, b) => a - b),
    }

    if (chartTicks.x.length > 30) {
      setZoomDomain({ x: [1, 30], y: [0, chartTicks.y[chartTicks.y.length - 1] + 1] })
    } else {
      setZoomDomain({
        x: [1, chartTicks.x.length],
        y: [0, chartTicks.y[chartTicks.y.length - 1] + 1],
      })
    }

    setChartTicks(chartTicks)
  }

  useEffect(() => {
    handleChartDataChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats])

  const handleSelectChart = (index: number, select = false) => {
    const newActiveCharts = [...activeCharts]

    if (select) {
      newActiveCharts[index] = 1
    } else {
      newActiveCharts[index] = !newActiveCharts[index] ? 1 : 0
    }
    setActiveCharts(newActiveCharts)

    const ticks = []

    if (newActiveCharts[0]) ticks.push(...deliveredData)
    if (newActiveCharts[1]) ticks.push(...canceledData)
    if (newActiveCharts[2]) ticks.push(...withProblemsData)

    const chartTicks = {
      x: ticks.map((tick) => tick.x),
      y: Array.from(new Set(ticks.map((tick) => tick.y))).sort((a, b) => a - b),
    }

    if (chartTicks.x.length > 30) {
      setZoomDomain({ x: [1, 30] })
    } else {
      setZoomDomain({ x: [1, chartTicks.x.length] })
    }

    setZoomDomain({ y: [0, chartTicks.y[chartTicks.y.length - 1] + 1] })

    setChartTicks(chartTicks)
  }

  const handleZoom = (domain: any) => {
    setZoomDomain(domain)
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Estatisticas">
      <LoadingSpinner text="Buscando estatísticas sobre as requisições de entrega. Aguarde..." />
      <Container className="px-5" style={{ paddingTop: 7 }}>
        <Row>
          <Col md={10} className="px-0">
            <Form>
              <Form.Group controlId="exampleForm.ControlSelect1">
                <Form.Select
                  className={styles.select}
                  value={selectedPeriod?.value || ''}
                  onChange={(e) => {
                    const selected = periodOptions.find((option) => option.value === e.target.value)
                    setSelectedPeriod(selected)
                  }}
                >
                  <option key="" value="" disabled>
                    Selecionar um período
                  </option>
                  {periodOptions.map((option, index) => (
                    <option key={option.value + index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Col>
          <Col md={2} className="mt-3 mt-md-0 px-0 px-md-3">
            <Button onClick={handleFilter} className={styles.filterBtn}>
              Filtrar
            </Button>
          </Col>
        </Row>

        {selectedPeriod.value === 'custom' && (
          <Row>
            <Col md={6} className="px-0 mt-3">
              <Form.Group controlId="exampleForm.ControlSelect1">
                <Form.Label className={styles.label}>Data inicial *</Form.Label>
                <Form.Control
                  className={styles.select}
                  type="date"
                  value={startDate?.toISOString().split('T')[0] || ''}
                  onChange={(e) => setStartDate(new Date(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="px-0 px-md-3 mt-3">
              <Form.Group controlId="exampleForm.ControlSelect1">
                <Form.Label className={styles.label}>Data final *</Form.Label>
                <Form.Control
                  className={styles.select}
                  type="date"
                  min={startDate?.toISOString().split('T')[0] || ''}
                  max={new Date().toISOString().split('T')[0]}
                  value={endDate?.toISOString().split('T')[0] || ''}
                  onChange={(e) => setEndDate(new Date(e.target.value))}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
        <Row>
          <Col md={6} className="px-0 mt-4">
            <Form.Group controlId="driver">
              <Form.Select
                className={styles.select}
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
              >
                <option key="" value="">
                  Selecionar um motorista
                </option>
                {drivers.map((driver) => (
                  <option key={driver.user_id} value={driver.user_id}>
                    {driver.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6} className="px-0 px-md-3 mt-4">
            <Form.Group controlId="vehicle">
              <Form.Select
                className={styles.select}
                onChange={(e) => setSelectedVehicleId(e.target.value)}
              >
                <option key="" value="">
                  Selecionar um veículo
                </option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.model} {vehicle.color} - {vehicle.board}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mt-4">
          <Row className="d-flex flex-row align-items-center px-0">
            <span className={styles.statisticsHeading}>Estatísticas do período</span>
            <span className={styles.statisticsText}>
              {selectedPeriod.value === 'today' ? (
                // Exibe a data atual
                new Date().toLocaleString('pt-BR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                })
              ) : selectedPeriod.value !== 'custom' ? (
                // Exibe period.startDate até period.endDate, se period !== 'custom'
                <>
                  {selectedPeriod.startDate &&
                    new Date(selectedPeriod.startDate).toLocaleString('pt-BR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      timeZone: 'UTC',
                    })}
                  {' até '}
                  {selectedPeriod.endDate
                    ? new Date(selectedPeriod.endDate).toLocaleString('pt-BR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'UTC',
                      })
                    : 'data atual'}
                </>
              ) : (
                // Se period === 'custom', exibe period.startDate até period.endDate
                <>
                  {startDate
                    ? startDate.toLocaleString('pt-BR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'UTC',
                      })
                    : '-'}
                  {' até '}
                  {endDate
                    ? endDate.toLocaleString('pt-BR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'UTC',
                      })
                    : '-'}
                </>
              )}
            </span>
          </Row>
        </Row>
        <Row className={`justify-content-center justify-content-md-between ${styles.cardsRow}`}>
          <Card
            className={styles.card}
            onClick={() => handleSelectChart(0)}
            style={{ borderColor: activeCharts[0] ? '#053C5E' : '#C6C6C6' }}
          >
            <span className={styles.cardNumber}>{stats?.delivered.length || 0}</span>
            <Circle className={styles.cardCircle} size={14} color="#44A80F" weight="fill" />
            <span>Entregas finalizadas</span>
          </Card>
          <Card
            className={styles.card}
            onClick={() => handleSelectChart(1)}
            style={{ borderColor: activeCharts[1] ? '#053C5E' : '#C6C6C6' }}
          >
            <span className={styles.cardNumber}>{stats?.canceled.length || 0}</span>
            <Circle className={styles.cardCircle} size={14} color="#DB222A" weight="fill" />
            <span>Entregas canceladas</span>
          </Card>
          <Card
            className={styles.card}
            onClick={() => handleSelectChart(2)}
            style={{ borderColor: activeCharts[2] ? '#053C5E' : '#C6C6C6' }}
          >
            <span className={styles.cardNumber}>{stats?.with_problems.length || 0}</span>
            <Circle className={styles.cardCircle} size={14} color="#FFC300" weight="fill" />
            <span>Entregas com problema</span>
          </Card>
        </Row>
        <Row className={styles.chartRow}>
          {!isLoading && (
            <Card className={styles.chartCard}>
              <VictoryChart
                theme={VictoryTheme.clean}
                padding={{ top: 20, bottom: 35, left: 50, right: 35 }}
                height={200}
                containerComponent={
                  <VictoryZoomContainer
                    zoomDimension="x"
                    zoomDomain={zoomDomain}
                    onZoomDomainChange={handleZoom}
                  />
                }
              >
                <VictoryAxis
                  tickValues={chartTicks.x}
                  style={{
                    tickLabels: { fontSize: 5, angle: -45, textAnchor: 'end' },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(tick) => Math.round(tick)}
                  tickValues={chartTicks.y}
                  style={{
                    tickLabels: { fontSize: 6 },
                    grid: {
                      stroke: '#CFD8DC',
                    },
                  }}
                />
                {activeCharts[0] &&
                  deliveredData?.length > 0 && [
                    <VictoryLine
                      data={deliveredData}
                      interpolation="linear"
                      style={{ data: { stroke: '#44A80F', strokeWidth: 1.2 } }}
                    />,
                    <VictoryScatter
                      data={deliveredData}
                      size={1.5}
                      style={{ data: { fill: '#44A80F' } }}
                    />,
                  ]}
                {activeCharts[1] &&
                  canceledData?.length > 0 && [
                    <VictoryLine
                      data={canceledData}
                      interpolation="linear"
                      style={{ data: { stroke: '#DB222A', strokeWidth: 1.2 } }}
                    />,
                    <VictoryScatter
                      data={canceledData}
                      size={1.5}
                      style={{ data: { fill: '#DB222A' } }}
                    />,
                  ]}
                {activeCharts[2] &&
                  withProblemsData?.length > 0 && [
                    <VictoryLine
                      data={withProblemsData}
                      interpolation="linear"
                      style={{ data: { stroke: '#FFC300', strokeWidth: 1.2 } }}
                    />,
                    <VictoryScatter
                      data={withProblemsData}
                      size={1.5}
                      style={{ data: { fill: '#FFC300' } }}
                    />,
                  ]}
              </VictoryChart>
            </Card>
          )}
        </Row>
      </Container>
    </DashboardLayout>
  )
}

export default EntregasEstatisticas
